import React, { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import PanelLayoutContent from "components/layout/PanelLayoutContent"
import { TslNewsletterService } from "services/api-workers-service"
import type { TslNewsletterSource } from "services/api-workers-service"
import { SentryService } from "services/SentryService"
import styled from "styled-components"
import { Box, Row } from "ui/components/atoms/_atoms"
import Button from "ui/components/atoms/button"
import Select from "ui/components/atoms/select"
import Spinner from "ui/components/atoms/spinner"
import Switch from "ui/components/atoms/switch"
import Tag from "ui/components/atoms/tag"
import { Label, P } from "ui/components/atoms/typography"
import Alert from "ui/components/molecules/alert"
import { Pagination } from "ui/components/molecules/pagination"
import { formatDate } from "utils/date"

import { localeToFlag, TSL_LANGUAGES } from "./data"
import TslNewsletterNav from "./TslNewsletterNav"
import SourceCreateModal from "./SourceCreateModal"

const PAGE_SIZE = 15

const SourcesList = () => {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(0)
  const [activeOnly, setActiveOnly] = useState(true)
  const [language, setLanguage] = useState<string>("")
  const [showCreate, setShowCreate] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const offset = page * PAGE_SIZE
  const { data, isLoading, isError } = useQuery({
    queryKey: ["tsl", "sources", { activeOnly, language, offset }],
    queryFn: () =>
      TslNewsletterService.listSources({
        active: activeOnly || undefined,
        language: language || undefined,
        limit: PAGE_SIZE,
        offset,
      }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => TslNewsletterService.deleteSource(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tsl", "sources"] })
    },
    onError: e => {
      SentryService.error("[ERROR TslNewsletterService.deleteSource]:", e)
      setActionError("Nie udało się dezaktywować źródła. Spróbuj ponownie.")
    },
  })

  const items = data?.items ?? []
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE))

  const handleDelete = (source: TslNewsletterSource) => {
    if (
      !window.confirm(
        `Dezaktywować źródło ${source.name}? Operacja usunie również powiązany feed w rss.app.`,
      )
    )
      return
    setActionError(null)
    deleteMutation.mutate(source.id)
  }

  return (
    <PanelLayoutContent
      title="TSL Newsletter"
      text="Źródła RSS dla newslettera TSL"
      button={{
        title: "Dodaj źródło",
        icon: "plus",
        onClick: () => setShowCreate(true),
      }}
    >
      <TslNewsletterNav />

      <FiltersWrapper $marginBottom="M">
        <Select
          name="language"
          label="Język"
          options={[["Wszystkie", ""], ...TSL_LANGUAGES]}
          value={language}
          onChange={({ value }) => {
            setPage(0)
            setLanguage(value)
          }}
          $minWidth="200px"
        />
        <ActiveSwitchWrapper>
          <Label $type="secondary" $marginBottom="XS">
            Tylko aktywne
          </Label>
          <Switch
            name="active"
            value={activeOnly}
            onChange={({ value }) => {
              setPage(0)
              setActiveOnly(value)
            }}
          />
        </ActiveSwitchWrapper>
      </FiltersWrapper>

      {actionError && (
        <Alert $type="error" $small $marginBottom="S">
          {actionError}
        </Alert>
      )}

      {isError && (
        <Alert $type="error" $marginBottom="S">
          Nie udało się pobrać listy źródeł. Sprawdź klucz API i połączenie.
        </Alert>
      )}

      {data && (
        <P>
          System znalazł <span>{data.total}</span> źródeł
        </P>
      )}

      {isLoading ? (
        <Row $justifyContent="center" $padding="M">
          <Spinner $size="30px" />
        </Row>
      ) : items.length === 0 ? (
        <EmptyState>
          <P $marginBottom="0">Brak źródeł dla wybranych filtrów.</P>
        </EmptyState>
      ) : (
        <ListWrapper>
          {items.map(source => (
            <SourceRow key={source.id}>
              <Row $alignItems="center" $width="100%" $justifyContent="space-between">
                <Box>
                  <Row $alignItems="center" $marginBottom="XXS">
                    <Label $marginBottom="0" $marginRight="XS">
                      {localeToFlag(source.language)} {source.name}
                    </Label>
                    <Tag
                      $type="secondary"
                      $showBorder={false}
                      $background={source.active ? "greenBackground" : "background"}
                      $color={source.active ? "green" : "third"}
                      label={source.active ? "Aktywne" : "Nieaktywne"}
                      $marginRight="XS"
                    />
                    {source.rss_app_feed_id ? (
                      <a
                        href={`https://rss.app/dashboard/feeds/${source.rss_app_feed_id}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "inherit", textDecoration: "none" }}
                      >
                        <Tag
                          $type="secondary"
                          $showBorder={false}
                          $background="primaryBackground"
                          $color="primary"
                          label="🔗 rss.app linked"
                          $marginRight="XS"
                        />
                      </a>
                    ) : (
                      <Tag
                        $type="secondary"
                        $showBorder={false}
                        $background="orangeBackground"
                        $color="orange"
                        label="⚠️ rss.app niezsynchronizowane"
                        $marginRight="XS"
                      />
                    )}
                    {!source.extract_events && (
                      <Tag
                        $type="secondary"
                        $showBorder={false}
                        $background="background"
                        $color="third"
                        label="Eventy: off"
                      />
                    )}
                  </Row>
                  <Row $alignItems="center">
                    <a
                      href={`https://${source.domain}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "inherit", textDecoration: "underline" }}
                    >
                      <P $small $marginBottom="0">
                        {source.domain}
                      </P>
                    </a>
                    {source.last_fetched_at && (
                      <P
                        $small
                        $marginBottom="0"
                        $marginLeft="S"
                        title={formatDate(source.last_fetched_at)}
                      >
                        Ostatni fetch: {formatDate(source.last_fetched_at)}
                      </P>
                    )}
                  </Row>
                </Box>
                {source.active && (
                  <Button
                    $type="secondary"
                    icon="close"
                    onClick={() => handleDelete(source)}
                    loading={deleteMutation.isPending}
                  >
                    Dezaktywuj
                  </Button>
                )}
              </Row>
            </SourceRow>
          ))}
        </ListWrapper>
      )}

      {data && data.total > PAGE_SIZE && (
        <Pagination defaultPage={page + 1} totalPages={totalPages} onChange={e => setPage(e - 1)} />
      )}

      <SourceCreateModal show={showCreate} setShow={setShowCreate} />
    </PanelLayoutContent>
  )
}

export default SourcesList

const FiltersWrapper = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  grid-gap: ${({ theme }) => theme.space.S};
`

const ActiveSwitchWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
`

const ListWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  grid-gap: ${({ theme }) => theme.space.XS};
`

const SourceRow = styled(Box)`
  padding: ${({ theme }) => theme.space.S};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  background: white;
`

const EmptyState = styled(Box)`
  padding: ${({ theme }) => theme.space.M};
  text-align: center;
  background: ${({ theme }) => theme.color.background};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  margin-bottom: ${({ theme }) => theme.space.M};
`
